import { UserManager, User, UserManagerSettings, Log } from 'oidc-client-ts';
import { ReadyChangeCallback, Session, SessionManager } from '../session-manager';
import { env_vars } from '../../load-env';

const required = (value: string | undefined, name: string): string => {
  if (!value) {
    throw new Error(`${name} is missing`);
  }
  return value;
};

export class OidcSession implements Session {

  config: UserManagerSettings = {
    client_id: required(env_vars.OIDC_CLIENT_ID, 'OIDC_CLIENT_ID'),
    redirect_uri: required(env_vars.OIDC_REDIRECT_URI, 'OIDC_REDIRECT_URI'),
    post_logout_redirect_uri: required(env_vars.OIDC_POST_LOGOUT_REDIRECT_URI, 'OIDC_POST_LOGOUT_REDIRECT_URI'),
    authority: required(env_vars.OIDC_AUTHORITY, 'OIDC_AUTHORITY'),
    scope: 'openid profile',
    response_type: 'code',
    ui_locales: 'nb',
    acr_values: env_vars.OIDC_ACR_VALUES,
    automaticSilentRenew: true
  };

  userManager: UserManager;
  user?: User | null;
  isLoading = true;

  readyChangeSubs: ReadyChangeCallback[] = [];

  constructor() {
    Log.setLogger(console);
    Log.setLevel(Log.ERROR);

    this.userManager = new UserManager(this.config);

    this.userManager.events.addAccessTokenExpired(() => {
      console.log('Access token expired. Signing out');
      SessionManager.endSession();
    });

    this.userManager.events.addUserSignedOut(async () => {
      console.log('Signed out from another service.');
      await this.userManager.signoutRedirect();
    });

    this.userManager.events.addUserLoaded((user) => {
      this.user = user;
    });
  }

  async start() {
    this.isLoading = true;

    try {
      const user = await this.userManager.getUser();
      this.user = user;

      if (!user || user.expired) {
        await this.signin();
        return false;
      }

      this.isLoading = false;
      this.readyChange();
      return true;

    } catch (err) {
      console.error('Failed to load user', err);
      await this.signin();
      return false;
    }
  }

  async signin() {
    await this.userManager.signinRedirect();
  }

  async end() {
    delete SessionManager.sessionType;
    await this.userManager.signoutRedirect();
  }

  async getAuthHeader() {
    if (!this.user) {
      this.user = await this.userManager.getUser();
    }

    return {
      Authorization: `Bearer ${this.user?.access_token ?? ''}`
    };
  }

  isValid() {
    return !!(this.user && !this.user.expired);
  }

  isReady() {
    return this.isValid() && !this.isLoading;
  }

  addReadyChangeListener(readyCallback: ReadyChangeCallback) {
    this.readyChangeSubs.push(readyCallback);
  }

  readyChange() {
    this.readyChangeSubs.forEach(callback => callback(this.isReady()));
  }
}
